import { useState } from 'react'
import { NavLink } from 'react-router-dom'
import { LayoutDashboard, Users, Settings as SettingsIcon, LogOut, Sun, Moon, Search } from 'lucide-react'
import { useAuth } from '../lib/AuthContext.jsx'
import { useTheme } from '../lib/useTheme.js'
import CommandPalette from './CommandPalette.jsx'

const navItems = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/clients', label: 'Clients', icon: Users },
  { to: '/settings', label: 'Settings', icon: SettingsIcon }
]

export default function AppShell({ children }) {
  const { profile, signOut } = useAuth()
  const { theme, toggleTheme } = useTheme()
  const [paletteOpen, setPaletteOpen] = useState(false)
  const orgName = profile?.organisations?.name || 'FlipStudio'

  return (
    <div className="min-h-screen grid" style={{ gridTemplateColumns: '260px 1fr' }}>
      <aside className="border-r border-line bg-panel/60 backdrop-blur-xl flex flex-col">
        <div className="flex items-center gap-3 px-5 py-5 border-b border-line">
          <div className="w-9 h-9 rounded-md bg-gradient-to-br from-blue to-[#2f6fd6] flex items-center justify-center font-display font-extrabold text-white shadow-glow">
            F
          </div>
          <div className="min-w-0">
            <div className="font-display font-bold text-[15px] leading-tight truncate">{orgName}</div>
            <div className="text-[11px] text-muted-2 uppercase tracking-wider">FlipStudio</div>
          </div>
        </div>

        <div className="px-3 pt-4">
          <button
            onClick={() => setPaletteOpen(true)}
            className="flex items-center gap-2.5 px-3 py-2.5 rounded-md text-[13px] text-muted-2 border border-line hover:border-line-strong hover:text-muted transition-colors duration-150 w-full"
          >
            <Search size={15} />
            <span className="flex-1 text-left">Search…</span>
            <kbd className="text-[10px] border border-line rounded px-1.5 py-0.5">⌘K</kbd>
          </button>
        </div>

        <nav className="flex-1 px-3 pt-3 pb-4 space-y-1">
          {navItems.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `relative flex items-center gap-3 pl-3 pr-3 py-2.5 rounded-md text-[14px] font-medium transition-colors duration-150 ${
                  isActive ? 'bg-panel2 text-ink' : 'text-muted hover:text-ink hover:bg-panel2/60'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <span
                      className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-full"
                      style={{ background: 'var(--accent-bar-active)' }}
                    />
                  )}
                  <Icon size={17} />
                  {label}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="px-3 py-4 border-t border-line space-y-1">
          <button
            onClick={toggleTheme}
            className="flex items-center gap-3 px-3 py-2.5 rounded-md text-[14px] font-medium text-muted hover:text-ink hover:bg-panel2/60 transition-colors duration-150 w-full"
          >
            {theme === 'dark' ? <Sun size={17} /> : <Moon size={17} />}
            {theme === 'dark' ? 'Light mode' : 'Dark mode'}
          </button>
          <button
            onClick={signOut}
            className="flex items-center gap-3 px-3 py-2.5 rounded-md text-[14px] font-medium text-muted hover:text-ink hover:bg-panel2/60 transition-colors duration-150 w-full"
          >
            <LogOut size={17} />
            Sign out
          </button>
        </div>
      </aside>

      <main className="min-w-0 overflow-y-auto">
        <div className="max-w-6xl mx-auto px-8 py-8">{children}</div>
      </main>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </div>
  )
}
